import {
  Directive,
  ElementRef,
  Renderer2,
  OnInit,
  signal,
  computed,
  inject,
  DestroyRef,
  effect,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Directive({
  selector: '[rpDynamicTitle]',
  standalone: true,
})
export class DynamicTitleDirective implements OnInit, OnDestroy, AfterViewInit {
  private _resizeSubject = new Subject<void>();
  private _resizeObserver: ResizeObserver;
  private _textContentObserver: MutationObserver;

  private _containerWidth = signal<number>(0);
  private _contentWidth = signal<number>(0);

  private _isOverflowing = computed(() => this._contentWidth() > this._containerWidth());

  private _elementRef = inject(ElementRef);
  private _renderer = inject(Renderer2);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this._setElementTitle();
    });
  }

  ngOnInit(): void {
    this._initResizeObserver();
    this._initResizeSubject();
    this._updateDimensions();
  }

  ngAfterViewInit(): void {
    this._initTextContentObserverObserver();
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._textContentObserver?.disconnect();
  }

  private _initResizeObserver(): void {
    const element = this._elementRef.nativeElement;

    this._resizeObserver = new ResizeObserver(() => {
      this._resizeSubject.next();
    });

    this._resizeObserver.observe(element);
  }

  private _initTextContentObserverObserver(): void {
    this._textContentObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          this._setElementTitle();
        }
      }
    });

    this._textContentObserver.observe(this._elementRef.nativeElement, {
      characterData: true,
      subtree: true,
    });
  }

  private _initResizeSubject(): void {
    this._resizeSubject.pipe(debounceTime(200), takeUntilDestroyed(this._destroyRef)).subscribe({
      next: this._updateDimensions.bind(this),
    });
  }

  private _updateDimensions(): void {
    const element = this._elementRef.nativeElement;
    const newContainerWidth = element.clientWidth;
    const newContentWidth = element.scrollWidth;

    if (this._containerWidth() !== newContainerWidth) {
      this._containerWidth.set(newContainerWidth);
    }

    if (this._contentWidth() !== newContentWidth) {
      this._contentWidth.set(newContentWidth);
    }
  }

  private _setElementTitle(): void {
    const element = this._elementRef.nativeElement;
    const textContent: string | null =
      typeof element.textContent === 'string' ? element.textContent.trim() : null;

    if (this._isOverflowing() && textContent) {
      this._renderer.setAttribute(element, 'title', textContent);
    } else {
      this._renderer.removeAttribute(element, 'title');
    }
  }
}
